import React from "react";
import { Button, ButtonProps, Modal, ModalProps, RequiredKeys, Row, useRequiredContext } from "@hex-insights/core";
import { FormType, useFormState } from "@hex-insights/forms";
import { useHistory } from "@hex-insights/router";
import { Icon } from "../../../../Components";
import { applicationDetails } from "../../../../settings";
import {
	MessageGroupCreateMutation,
	Student,
	useOrderedLabels,
	User,
	UserGroup,
	useStudentNamesAndImagesQuery,
} from "../../../../Utilities";
import { MessageFormValues } from "../../../FormUtilities";
import { MessageSendingInformationContext } from "../Context";
import { Create } from "../Create";

enum Page {
	Topic,
	Message,
}

export type SendMessageButtonProps = Pick<ButtonProps, "variant" | "size" | "className" | "style" | "children"> & {
	userID: User["id"];
};

export function SendMessageButton({
	variant,
	size,
	userID,
	className,
	style,
	children = (
		<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
			<Icon.PaperPlaneTilt size={16} /> Send Message
		</Row>
	),
}: SendMessageButtonProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const initialUserRecipientIDs = React.useMemo(() => [userID], [userID]);

	return (
		<React.Fragment>
			<Button variant={variant} size={size} onClick={openModal} className={className} style={style}>
				{children}
			</Button>

			<Modal.If condition={isModalOpen}>
				<NewMessageModal initialUserRecipientIDs={initialUserRecipientIDs} onClose={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}

type NewMessageFormValues = Pick<MessageFormValues.Create, "body" | "mediaAttachments">;

const defaultInitialUserRecipientIDs: User["id"][] = [];
const defaultInitialUserGroupRecipientIDs: UserGroup["id"][] = [];

export type NewMessageModalProps = {
	initialUserRecipientIDs?: User["id"][];
	initialUserGroupRecipientIDs?: User["id"][];
} & RequiredKeys<Pick<ModalProps, "ifRef" | "onClose">, "onClose">;

export function NewMessageModal({
	initialUserRecipientIDs = defaultInitialUserRecipientIDs,
	initialUserGroupRecipientIDs = defaultInitialUserGroupRecipientIDs,
	ifRef,
	onClose,
}: NewMessageModalProps) {
	const { viewerID, messagingMessageGroupPageInfo } = useRequiredContext(
		MessageSendingInformationContext,
		"MessageGroupComponents.NewMessageModal",
	);

	const [userRecipientIDs, setUserRecipientIDs] = React.useState<User["id"][]>(initialUserRecipientIDs);
	const [userGroupRecipientIDs, setUserGroupRecipientIDs] = React.useState<User["id"][]>(initialUserGroupRecipientIDs);
	const [selectedStudentIDs, setSelectedStudentIDs] = React.useState<Student["id"][]>([]);

	const formState = useFormState<NewMessageFormValues>({
		initialFormValues: { body: "", mediaAttachments: [] },
		formType: FormType.Create,
	});

	const { push: pushHistory } = useHistory();
	const onSuccess = React.useCallback(
		(data?: MessageGroupCreateMutation["createMessageGroup"] | null) => {
			if (data) {
				pushHistory(messagingMessageGroupPageInfo.to(data.id));
			}
		},
		[pushHistory, messagingMessageGroupPageInfo],
	);

	const { data: studentData } = useStudentNamesAndImagesQuery({
		variables: {
			filters: {
				or: [
					{
						idIn: selectedStudentIDs,
					},
					{
						relationships: [
							{
								guardian: [{ person: [{ userIDIn: userRecipientIDs }] }],
							},
						],
					},
				],
			},
		},
		skip: (selectedStudentIDs.length === 0 && userRecipientIDs.length === 0) || userGroupRecipientIDs.length > 0,
	});
	const lastStudentDataRef = React.useRef(studentData);
	const availableTopicStudents = React.useMemo(() => {
		const data = studentData ?? lastStudentDataRef.current;
		if (studentData) {
			lastStudentDataRef.current = studentData;
		}
		return data?.studentConnection.edges.map((e) => e.node) ?? [];
	}, [studentData]);

	const { loading: loadingRecipients, data: recipientData } = Create.InternalPortal.useRecipients();
	const { availableUserRecipients, availableUserGroupRecipients } = React.useMemo(
		() => ({
			availableUserRecipients: recipientData?.userConnection.edges.map((e) => e.node) ?? [],
			availableUserGroupRecipients: recipientData?.userGroupConnection.edges.map((e) => e.node) ?? [],
		}),
		[recipientData],
	);

	const contextValue = React.useMemo<Create.MessageGroupCreateContextValue>(
		() => ({
			selectedUserRecipients: availableUserRecipients.filter((e) => userRecipientIDs.includes(e.id)),
			selectedUserGroupRecipients: availableUserGroupRecipients.filter((e) => userGroupRecipientIDs.includes(e.id)),
			selectedTopicStudents: availableTopicStudents.filter((e) => selectedStudentIDs.includes(e.id)),
		}),
		[
			availableUserRecipients,
			userRecipientIDs,
			availableUserGroupRecipients,
			userGroupRecipientIDs,
			availableTopicStudents,
			selectedStudentIDs,
		],
	);

	const showTopicPage =
		userGroupRecipientIDs.length === 0 && contextValue.selectedUserRecipients.some((e) => !!e.person?.guardian);

	const pages = React.useMemo(() => {
		if (showTopicPage) {
			return [Page.Topic, Page.Message];
		}
		return [Page.Message];
	}, [showTopicPage]);
	const {
		label: page,
		labelIndex: pageIndex,
		incrementLabel: incrementPage,
		decrementLabel: decrementPage,
	} = useOrderedLabels(pages);

	if (loadingRecipients) {
		return (
			<Create.NewMessageLoadingModal ifRef={ifRef} onClose={onClose}>
				Fetching recipient information...
			</Create.NewMessageLoadingModal>
		);
	}

	if (page === Page.Topic) {
		return (
			<Create.MessageGroupCreateContext.Provider value={contextValue}>
				<Create.NewMessageStudentTopicWithAdditionalRecipientsModal
					selectedStudentIDs={selectedStudentIDs}
					setSelectedStudentIDs={setSelectedStudentIDs}
					availableTopicStudents={availableTopicStudents}
					userRecipientIDs={userRecipientIDs}
					setUserRecipientIDs={setUserRecipientIDs}
					userGroupRecipientIDs={userGroupRecipientIDs}
					setUserGroupRecipientIDs={setUserGroupRecipientIDs}
					RecipientSections={Create.InternalPortal.AdditionalMessageRecipientSections}
					onNextClick={incrementPage}
					ifRef={ifRef}
					onClose={onClose}
					confirmOnClose={false}
				/>
			</Create.MessageGroupCreateContext.Provider>
		);
	}

	if (applicationDetails.portal === "admin-portal" || applicationDetails.portal === "teacher-portal") {
		return (
			<Create.MessageGroupCreateContext.Provider value={contextValue}>
				<Create.NewMessageSubmittableMessageModal
					formState={formState}
					userID={viewerID}
					userRecipientIDs={userRecipientIDs}
					userGroupRecipientIDs={userGroupRecipientIDs}
					selectedStudentIDs={selectedStudentIDs}
					body={formState.formValues.body}
					mediaAttachments={formState.formValues.mediaAttachments}
					setUserRecipientIDs={setUserRecipientIDs}
					RecipientSections={MessageEditRecipientSections}
					onBackClick={pageIndex > 0 ? decrementPage : undefined}
					ifRef={ifRef}
					onClose={onClose}
					onSuccess={onSuccess}
				/>
			</Create.MessageGroupCreateContext.Provider>
		);
	}

	if (page === Page.Message) {
		return (
			<Create.MessageGroupCreateContext.Provider value={contextValue}>
				<Create.NewMessageMessageModal
					formState={formState}
					userGroupRecipientIDs={userGroupRecipientIDs}
					userRecipientIDs={userRecipientIDs}
					setUserRecipientIDs={setUserRecipientIDs}
					RecipientSections={MessageEditRecipientSections}
					onNextClick={incrementPage}
					onBackClick={pageIndex > 0 ? decrementPage : undefined}
					ifRef={ifRef}
					onClose={onClose}
				/>
			</Create.MessageGroupCreateContext.Provider>
		);
	}

	return (
		<Create.MessageGroupCreateContext.Provider value={contextValue}>
			<Create.NewMessageReviewModal
				userID={viewerID}
				userRecipientIDs={userRecipientIDs}
				userGroupRecipientIDs={userGroupRecipientIDs}
				selectedStudentIDs={selectedStudentIDs}
				body={formState.formValues.body}
				mediaAttachments={formState.formValues.mediaAttachments}
				onBackClick={decrementPage}
				ifRef={ifRef}
				onClose={onClose}
				onSuccess={onSuccess}
			/>
		</Create.MessageGroupCreateContext.Provider>
	);
}

function MessageEditRecipientSections(props: Create.MessageEditRecipientSectionsProps) {
	if (applicationDetails.portal === "admin-portal") {
		return <Create.InternalPortal.MessageEditRecipientSections {...props} variant="admin" />;
	}
	if (applicationDetails.portal === "teacher-portal") {
		return <Create.InternalPortal.MessageEditRecipientSections {...props} variant="teacher" />;
	}
	return <Create.ParentPortal.MessageEditRecipientSections {...props} />;
}
